import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNotification } from "config/Notification";

import {
  getSingleAlert as getSingleAlertService,
  getAvailableFields as getAvailableFieldsService,
  updateBulkAlertsById as updateBulkAlertsByIdService,
  getUpdatedTime as getUpdatedTimeService,
  getAgents as getAgentsService,
  getAutocompleteResults as getAutocompleteResultsService,
  getThreatHuntingDetails as getThreatHuntingDetailsService,
} from "services/modules/soc/agent";

import { DatePicker, Space } from "antd";
import dayjs from "dayjs";

import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import * as XLSX from "xlsx";
import CustomPagination from "components/CustomPagination";
import { Form, Button, Card, Accordion, Modal, OverlayTrigger, Tooltip, Spinner, FloatingLabel } from "react-bootstrap";
import { FaChevronDown, FaChevronUp, FaDownload, FaExclamationTriangle, FaTimes } from "react-icons/fa";
import Select from "react-select";
import { CiCircleInfo } from "react-icons/ci";

const { RangePicker } = DatePicker;

dayjs.extend(timezone);
dayjs.extend(utc);

const ReportManagement = () => {
  // State Variable
  const [agents, setAgents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [severity, setSeverity] = useState("critical");
  const [agentId, setAgentId] = useState("");
  const [viewAlert, setViewAlert] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState([]);
  const [expand, setExpand] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [alertStatus, setAlertStatus] = useState(false);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [newPageNumber, setNewPageNumber] = useState();
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [searchQuery, setSearchQuery] = useState(null);
  const [isAddFilters, setIsAddFilters] = useState(false);
  const [filterOptions, setFilterOption] = useState([]);
  const [filters, setFilters] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [isSOCENV, setIsSOCENV] = useState(true);
  const [startDateTime, setStartDateTime] = useState(null);
  const [endDateTime, setEndDateTime] = useState(null);
  const [maxValue, setMaxValue] = useState(null);
  const [alertTimestamp, setAlertTimestamp] = useState();
  const [options, setOptions] = useState([]);
  const [openAccordions, setOpenAccordions] = useState({});
  const severityOptions = ["all", "low", "medium", "high", "critical"];
  const name = "securityAlert";

  // Redux selectors
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);

  // Notification
  const { openNotification } = useNotification();

  // Handles the severity based on rule levels
  const getSeverityLabel = useCallback((ruleLevel) => {
    if (ruleLevel >= 0 && ruleLevel <= 6) {
      return "Low";
    } else if (ruleLevel >= 7 && ruleLevel <= 8) {
      return "Medium";
    } else if (ruleLevel >= 9 && ruleLevel <= 11) {
      return "High";
    } else if (ruleLevel >= 12) {
      return "Critical";
    }
    return "";
  }, []);

  useEffect(() => {
    if (agentId) {
      fetchUpdateTime();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  useEffect(() => {
    setAgents([]);
    setAgentId("");
    fetchAgents();
    setAlerts([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyContainer?.soc_group, selectedCompanyContainer?.soc_env]);

  useEffect(() => {
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyContainer?.soc_env]);

  useEffect(() => {
    if (agentId) {
      fetchAlertsByAgentId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    severity,
    agentId,
    rowsPerPage,
    page,
    selectedFilters,
    startDateTime, endDateTime
  ]);

  useEffect(() => {
    if (severity === "critical" && alerts?.length === 0) {
      setSeverity("high");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alerts]);

  useEffect(() => {
    setFilterOption((prevOptions) =>
      propertyOptions.filter(
        (option) =>
          !properties.includes(option) &&
          ![
            "rule.description",
            "@timestamp",
            "agent.ip",
            "agent.name",
          ].includes(option)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties]);

  // Method resets checkboxes  when alertId changes
  useEffect(() => {
    handleCancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  useEffect(() => {
    handleClearFilters();
  }, [selectedCompanyContainer]);

  useEffect(() => {
    let start, end;

    if (alertTimestamp) {
      end = convertToIST(new Date(alertTimestamp).toISOString());

      const parsedAlertDate = new Date(alertTimestamp);
      const now = new Date();

      // Check if alertTimestamp is valid
      if (isNaN(parsedAlertDate.getTime())) {
        console.error("Invalid Date:", alertTimestamp);
        setMaxValue(now); // fallback to current time
      } else {
        const latestDate = new Date(Math.max(parsedAlertDate.getTime(), now.getTime()));
        setMaxValue(latestDate);
      }
    } else {
      end = new Date();
      end.setHours(0, 0, 0, 0);
    }

    start = convertToIST(new Date(end.getTime() - 24 * 60 * 60 * 1000).toISOString()); // Set start to 24 hours before end

    setStartDateTime(start);
    setEndDateTime(end);
  }, [alertTimestamp]);

  const convertToIST = (dateString) => {
    // Check if string includes an explicit offset (e.g., +, -)
    if (dateString.includes('+') || dateString.includes('-')) {
      // Parse as is and convert to IST, returning a Date object
      return dayjs(dateString).tz("Asia/Kolkata", true).toDate();
    } else {
      // Assume it's UTC if it has 'Z', convert to IST and return a Date object
      return dayjs.utc(dateString).tz("Asia/Kolkata", true).toDate();
    }
  };
  // Handles to get alerts updatedTime By agent
  const fetchUpdateTime = async () => {
    const params = { agentId: agentId, soc_env: selectedCompanyContainer?.soc_env };
    const response = await getUpdatedTimeService(params);
    if (response?.data?.[0]) {
      setAlertTimestamp(response?.data?.[0]?.alert_timestamp)
    } else {
      setAlertTimestamp(null)
    }
  };
  // Method handles the  agents
  const fetchAgents = async () => {
    const params = {
      group: selectedCompanyContainer?.soc_group,
      soc_env: selectedCompanyContainer?.soc_env,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      sort: "id",
    };

    try {
      const data = await getAgentsService(params);
      if (
        data &&
        data?.data?.affected_items &&
        selectedCompanyContainer?.soc_env
      ) {
        const agentData = data?.data?.affected_items?.map((agent) => ({
          agent_name: agent.name,
          agent_id: agent.id,
        }));
        setAgents(agentData ?? []);
        setAgentId(agentData?.[0]?.agent_id || "");
        // setAgentId(agentData?.[0]?.agent_id || "004");
      } else if (!selectedCompanyContainer?.soc_env) {
        setIsSOCENV(false);
        setAgents([]);
        setAgentId("");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Method handles the alerts based on agent id
  const fetchAlertsByAgentId = async () => {
    try {
      setLoading(true);
      setAlerts([]);
      const body = {
        agentId: agentId,
        pagestart: page * rowsPerPage,
        limit: rowsPerPage,
        severity: severity,
        soc_env: selectedCompanyContainer?.soc_env,
        sort: "asc",
      };
      if (selectedFilters && selectedFilters.length > 0) {
        const keypairs = selectedFilters?.map((filter) => ({
          key: filter.property,
          value: filter.value
        }));
        body.searchPairs = keypairs
      }
      if (startDateTime && endDateTime) {
        const sDate = startDateTime;
        const eDate = endDateTime;
        body.start = sDate;
        body.end = eDate;
      }
      const response = await getThreatHuntingDetailsService(body);
      setTotalCount(response?.totalRecords || 0);
      if (response && response.data.hits.hits.length > 0 && selectedCompanyContainer?.soc_env) {
        const groupedData = response.data.hits.hits.map((hit) => {
          return {
            timestamp: formatTime(hit._source.timestamp),
            agentName: hit._source.agent.name,
            ruleName: hit._source.rule.id,
            ruleDescription: hit._source.rule.description,
            ruleLevel: hit._source.rule.level,
            alertId: hit._id,
            agentip: hit._source.agent.ip,
            selected: false,
            alertstatus: hit._source.alert
              ? hit._source.alert.status
                ? hit._source.alert.status
                : false
              : false,
            source: hit._source,
            index: hit._index,
          };
        });
        setAlerts(groupedData ?? []);
        setLoading(false);
      } else if (!selectedCompanyContainer?.soc_env) {
        setIsSOCENV(false);
        setAlerts([]);
      } else {
        setAlerts([])
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Method handles the available properties of alerts
  const fetchProperties = async () => {
    try {
      const params = { soc_env: selectedCompanyContainer?.soc_env };
      if (!params.soc_env) {
        setIsSOCENV(false);
      } else {
        setIsSOCENV(true);
        const response = await getAvailableFieldsService(params);
        setPropertyOptions(response?.properties);
        const filteredOptions = response?.properties?.filter(
          (option) =>
            ![
              "rule.description",
              "@timestamp",
              "agent.ip",
              "agent.name",
            ].includes(option)
        );

        setFilterOption(filteredOptions ?? []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Handles page change
  const handleChangePage = (event, newPage) => {
    if (alerts?.some((alert) => alert.selected === true)) {
      setNewPageNumber(newPage);
      setOpen(true);
    } else {
      setPage(newPage);
    }
    setIsAllSelected(false);
    setAlertStatus("false");
  };

  // Handles change in rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10)); // Update the rows per page when the user selects a new option
    setPage(0); // Reset the page to 0 when rows per page changes
  };

  // Handles the view alert icon
  const viewAlerts = (alert) => {
    setViewAlert(true);
    setDialogOpen(true);
    fetchSingleAlert(alert?.alertId);
  };

  // Method handles to view single alert details
  const fetchSingleAlert = async (alertId) => {
    try {
      const params = {
        id: alertId,
        soc_env: selectedCompanyContainer?.soc_env,
      };
      const response = await getSingleAlertService({ params });
      setSelectedAlert(response?.data);
      return response?.data;
    } catch (error) {
      console.error(error);
    }
  };

  const renderTTPsValue = (key, value) => {
    if (Array.isArray(value)) {
      return value.join(", ");
    }
    if (typeof value === "object") {
      return <pre>{JSON.stringify(value, null, 2)}</pre>;
    }
    if (key === "level") {
      const severityLabel =
        value >= 0 && value <= 6
          ? "Low"
          : value >= 7 && value <= 8
            ? "Medium"
            : value >= 9 && value <= 11
              ? "High"
              : "Critical";

      return (
        <span style={{ color: getSeverityColor(value), fontWeight: "bold" }}>
          {severityLabel}
        </span>
      );
    }
    return value || "N/A";
  };

  // Handles the severity color
  const getSeverityColor = (severity) => {
    if (severity >= 0 && severity <= 6) return "#4baeec"; // Low
    if (severity >= 7 && severity <= 8) return "#4e4ece"; // Medium
    if (severity >= 9 && severity <= 11) return "#fc659c"; // High
    if (severity >= 12) return "red"; // Critical
    return "black"; // Default color
  };

  // Method handles the format of time
  const formatTime = (key) => {
    if (key) {
      const date = new Date(key);
      let istDate;
      // Regex to match both formats: +0530 and +05:30
      const timezoneOffsetRegex = /[+-]\d{2}:?\d{2}$/;

      // Check if the timestamp contains a valid timezone offset (either +0530 or +05:30)
      if (key.match(timezoneOffsetRegex)) {
        // If the timestamp includes "+0530" or "+05:30", it's already in IST
        istDate = date;
      } else if (key.includes("z") || key.includes("+0000")) {
        // If the timestamp is UTC (includes "z" or "+0000"), convert to IST
        istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
      } else {
        // If the timestamp doesn't match any known formats, handle as needed
        istDate = date;
      }
      return `${istDate.toLocaleString("en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })}.${istDate.getMilliseconds().toString().padStart(3, "0")}`
        .replace(",", "")
        .replace(" at", " @");
    }
  };

  // Method handles the format of alert property
  const formatPropertyName = (key) => {
    if (key === "level") return "Severity";
    else if (key === "ip" || key === "id") return key.toUpperCase();
    else if (key === "cve") return "CVE";
    else if (key === "cvss3") return "CVSS3 Score";
    else return key.charAt(0).toUpperCase() + key.slice(1);
  };

  // Method handles the format of alert key values
  const renderValue = (value) => {
    if (Array.isArray(value)) {
      if (value.every((item) => typeof item === "string")) {
        return value.join(", ");
      }
      return value.map((item, idx) => (
        <div key={`array-${idx}`} className="my-1">
          {renderValue(item)}
        </div>
      ));
    }

    if (typeof value === "object" && value !== null) {
      return Object.entries(value).map(([key, val], idx) => (
        <div key={`key-${idx}`} className="my-1">
          {key}: {renderValue(val)}
        </div>
      ));
    }
    return value;
  };

  // Handles the multiple selection of checkbox
  const toggleAll = (checked) => {
    setIsAllSelected(checked);
    const updatedData = alerts.map((row) => ({
      ...row,
      selected: checked,
    }));
    setAlerts(updatedData);
  };

  // Handles the individual row checkboxes
  const toggleRow = (id) => {
    const updatedData = alerts.map((row) =>
      row.alertId === id ? { ...row, selected: !row.selected } : row
    );
    setAlerts(updatedData);
  };

  // Handles the multiple selection of alert status
  const toggleAllStatus = (value) => {
    setAlertStatus(value);
    const updatedData = alerts.map((row) => ({
      ...row,
      alertstatus: row.selected ? value : row.alertstatus,
    }));
    setAlerts(updatedData);
  };

  // Handles the individual alert status
  const toggleAlertStatus = (id, value) => {
    const updatedData = alerts.map((row) =>
      row.alertId === id ? { ...row, alertstatus: value } : row
    );
    setAlerts(updatedData);
  };

  // Handles to track the selected properties
  const handleChange = (event, value) => {
    setProperties(value);
    setFilterOption(
      propertyOptions?.filter(
        (option) =>
          !value.includes(option) &&
          ![
            "rule.description",
            "@timestamp",
            "agent.ip",
            "agent.name",
            "@timestamp",
            "@version"
          ].includes(option)
      )
    );
  };

  // Handles changes in search for Add Filters
  const handleSearchChange = (event, value) => {
    setSearchQuery(value);
    setPage(0);
  };

  // Method to get the property values based on user selected property
  const getPropertyValue = (obj, path) => {
    const modifiedPath = path.replace("@", "");
    const paths = modifiedPath.split(".");
    let value = obj;

    for (const path of paths) {
      if (value && value[path] !== undefined) {
        value = value[path];
      } else {
        return "-";
      }
    }
    return Array.isArray(value) || typeof value === "string"
      ? value
      : typeof value === "object"
        ? "-"
        : String(value);
  };

  // Method handles to update the status of alerts
  const handleSubmit = async () => {
    const selected = alerts?.some((alert) => alert.selected === true);
    if (selected) {
      const selectedAlerts = alerts.filter((alert) => alert.selected === true);
      if (selectedAlerts.length > 0) {
        const selectedAlert = selectedAlerts[selectedAlerts.length - 1];

        const timestamp = selectedAlert.timestamp;
        const localDate = new Date(timestamp);

        // Check if the localDate is valid
        if (isNaN(localDate.getTime())) {
          console.error("Invalid Date:", timestamp);
        } else {
          // Convert to UTC by calling toISOString()
          const utcDate = localDate.toISOString();

          const updatedAlerts = selectedAlerts.map((data) => ({
            id: data.alertId,
            index: data.index,
            updateFields: {
              alert: {
                status: data.alertstatus === "true",
              },
            },
            agentName: selectedAlert.agentName,
            agentId: agentId,
            timestamp: utcDate,
            group: selectedCompanyContainer?.soc_group,
          }));
          const socEnv = selectedCompanyContainer?.soc_env;
          const response = await updateBulkAlertsByIdService(socEnv, updatedAlerts);
          if (response.status === 200) {
            openNotification(response?.data?.message, "success");
            setTimeout(() => {
              fetchUpdateTime();
              fetchAlertsByAgentId();
              setIsAllSelected(false);
            }, 3000);
          }
        }
      }
    } else {
      openNotification("Please select alert and update the status", "error");
    }

  }
  // MEthod handles to save the changes before moving to next page
  const handleSaveChanges = () => {
    setPage(newPageNumber);
    setNewPageNumber(null);
    setOpen(false);
  };

  // Method handles to reset the checkboxes
  const handleCancel = () => {
    setOpen(false);
    setNewPageNumber(null);
    const updateAlerts = alerts?.map((row) => ({
      ...row,
      selected: false,
    }));
    setAlerts(updateAlerts);
    setIsAllSelected(false);
  };

  // Handles the filters and search input
  const handleSelectedFilters = (input) => {
    if (!selectedFilters) {
      setSelectedFilters([]);
    }
    setSelectedFilters((prev) => {
      if (!filters) return prev;

      const updatedFilters =
        prev?.filter((item) => item.property !== filters) || [];

      if (input === "") {
        openNotification("Please enter the search value", "error");
        return updatedFilters;
      }
      return [...updatedFilters, { property: filters, value: input }];
    });
  };

  // Handles to remove selected or applied filter
  const handleRemoveFilter = (property) => {
    setSelectedFilters((prev) =>
      prev.filter((filter) => filter.property !== property)
    );
    setFilters(null);
  };

  // Handles to clear all applied filters
  const handleClearFilters = () => {
    setFilters(null);
    setSelectedFilters([]);
    setSearchQuery(null);
    setIsAddFilters(false);
  };

  // Handles to fetch the results based on the selected property
  const fetchOptions = async (value) => {
    try {
      const response = await getAutocompleteResultsService({ agentId: agentId, field: value, start: startDateTime, end: endDateTime, soc_env: selectedCompanyContainer?.soc_env, severity: severity })
      setOptions(response?.data || []);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  // Handles to remove dynamic columns 
  const handleRemoveProp = (key) => {
    setProperties((prevProperties) => {
      const updatedProperties = prevProperties.filter((prop) => prop !== key);
      return updatedProperties;
    });

    setFilterOption((prevFilterOptions) => {
      if (!prevFilterOptions.includes(key)) {
        return [...prevFilterOptions, key].filter(
          (option) =>
            ![
              "rule.description",
              "@timestamp",
              "agent.ip",
              "agent.name",
            ].includes(option)
        );
      }
      return prevFilterOptions;
    });
  };

  // Handles to format the object keys and values
  const renderSourceDataValue = (value, parentKey = "") => {
    if (Array.isArray(value)) {
      // Array of strings
      if (value.every((item) => typeof item === "string")) {
        return value.join(", ");
      }
      //  array of objects
      return value.map((item, idx) => (
        <div key={`${parentKey}-array-${idx}`} className="my-1">
          {renderValue(item, parentKey)}
        </div>
      ));
    }

    // object
    if (typeof value === "object" && value !== null) {
      return Object.entries(value).map(([key, val], idx) => {
        const newKey = parentKey ? `${parentKey}-${key}` : key;

        return (
          <div className="row my-2" key={`${newKey}-${idx}`}>
            <div className="col-5 text-body1">
              <strong className="word-wrap my-2">{newKey}</strong>:
            </div>
            <div className="col-7 text-body1 text-grey-8 word-wrap">
              {renderValue(val, newKey)}
            </div>
          </div>
        );
      });
    }
    // string
    return value;
  };

  // Handles to flatten the nested objects
  const flattenObject = (obj, parentKey = "", result = {}) => {
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        let newKey = parentKey ? `${parentKey}.${key}` : key;

        if (typeof obj[key] === "object" && obj[key] !== null) {
          if (Array.isArray(obj[key])) {
            result[newKey] = obj[key].join(", "); // Join array values into a string
          } else {
            flattenObject(obj[key], newKey, result);
          }
        } else {
          result[newKey] = obj[key];
        }
      }
    }
    return result;
  };

  // Handles to downlaod excel file of single alert data
  const exportToExcel = async (alertData) => {
    try {
      if (alertData) {
        const dataToExport = flattenObject(alertData?._source);
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet([dataToExport]);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
        let filename = `${dataToExport["rule.description"]}.xlsx`;
        XLSX.writeFile(workbook, filename);
      }
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    }
  };

  // Handles to download the excel report based on the alert id
  const handleDownloadExcel = async (id) => {
    const data = await fetchSingleAlert(id);
    if (data) {
      exportToExcel(data);
    } else {
      console.warn("no data is available to download");
    }
  };

  // Handles to toggle accordionsr
  const toggleAccordion = (key) => {
    setOpenAccordions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };
  return (
    <>
      <div className="m-2 ">
        <div className="row header-cont p-2">
          <div className="col-xs-12 col-sm-12 col-md-6 col-lg-3 partner-cont">
            <img
              src="../assets/partners.svg"
              className="card-icon"
              alt="logo"
            />
            <span className="icon-color text-h5 text-bold font">
              View Alerts
            </span>
          </div>
        </div>
        <hr className="w-100 my-3" />
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center mt-5"
          >
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <>

            <div className=" row my-2">

              <div
                className="col-lg-6 col-md-6 col-sm-12 search-ecs date-picker"
                style={{ zIndex: "100" }}
              >
                <div className="d-flex align-items-center flex-nowrap gap-2">
                  {/* Label */}
                  <label className="mb-0 me-2">Date Range:</label>

                  {/* Date Picker */}
                  <div className="w-full max-w-xl flex flex-row gap-4">
                    <Space direction="vertical" size={12}>
                      <RangePicker
                        showTime
                        value={[dayjs(startDateTime), dayjs(endDateTime)]}
                        format="YYYY-MM-DD HH:mm:ss.SSS"
                        onChange={(dates) => {
                          if (dates && dates[0] && dates[1]) {
                            const start = convertToIST(dates[0].toDate().toISOString());
                            const end = convertToIST(dates[1].toDate().toISOString());

                            // Custom range validation
                            const diffInMs = end.getTime() - start.getTime();

                            if (diffInMs < 0) {
                              openNotification(
                                "Start date must be before end date",
                                "warning"
                              );
                              return;
                            }
                            setStartDateTime(start);
                            setEndDateTime(end);
                          }
                        }}
                        disabledDate={(current) => {
                          // Disable future dates beyond maxValue
                          if (!maxValue) return false;
                          return current && current > dayjs(maxValue.toString());
                        }}
                      />
                    </Space>
                  </div>
                  {alertTimestamp && (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-download">
                          Last Updated Alert Time:&nbsp;
                          <strong>{formatTime(alertTimestamp)}</strong>
                        </Tooltip>
                      }
                    >
                      <span style={{ cursor: 'pointer' }} className="ms-2">
                        <CiCircleInfo color="green" size={26} />
                      </span>
                    </OverlayTrigger>
                  )}
                </div>
              </div>

              <div className=" col-xl-2 col-lg-2 col-md-2 col-sm-4 col-xs-6 ms-auto d-flex justify-content-end align-items-center mt-2">
                <Select
                  isMulti
                  options={filterOptions.map((opt) => ({ label: opt, value: opt }))}
                  value={properties.map((opt) => ({ label: opt, value: opt }))}
                  onChange={(selected) => {
                    handleChange(null, selected ? selected.map((opt) => opt.value) : []);
                  }}
                  placeholder="Available Fields"
                  noOptionsMessage={() => "No matching fields"}
                  classNamePrefix="select"
                  styles={{
                    container: (base) => ({
                      ...base,
                      width: "100%",
                    }),
                    control: (base) => ({
                      ...base,
                      minHeight: "36px",
                      width: "100%",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: "2px 8px",
                      overflow: "hidden",
                    }),
                    multiValue: () => ({
                      display: "none",
                    }),
                    option: (base) => ({
                      ...base,
                      fontSize: "1rem",
                      fontWeight: "400",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      fontSize: "0.9rem",
                    }),
                  }}
                />
              </div>
            </div>

            <div className="row ">
              <div className=" col-lg-2 col-md-3 col-sm-6 col-xs-6 search-ecs">
                <div className="custom-floating-select">
                  <FloatingLabel controlId="floatingSelect" label="Severity">
                    <Form.Select
                      size="sm"
                      value={severity}
                      onChange={(e) => {
                        setSeverity(e.target.value);
                        setPage(0);
                      }}
                    >
                      {severityOptions?.map((option) => (
                        <option key={option} value={option}>
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                </div>
                {/* </Form.Group> */}
              </div>
              <div className=" col-lg-2 col-md-3 col-sm-6 col-xs-6 search-ecs">
                <div className="custom-floating-select">
                  <FloatingLabel controlId="floatingSelect" label="Agents">
                    <Select
                      options={agents.map(agent => ({
                        value: agent.agent_id,
                        label: agent.agent_name,
                      }))}
                      value={
                        agents
                          .map(agent => ({ value: agent.agent_id, label: agent.agent_name }))
                          .find(opt => opt.value === agentId) || null
                      }
                      onChange={(selected) => {
                        setAgentId(selected ? selected.value : "");
                        setPage(0);
                      }}
                      isClearable
                      noOptionsMessage={() => "No agents found"}
                      styles={{
                        option: (base) => ({
                          ...base,
                          fontSize: "1rem",
                          fontWeight: "400",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          fontSize: "0.875rem",
                          fontWeight: "400",
                        }),
                      }}
                    />
                  </FloatingLabel>
                </div>
                {/* /> */}
              </div>
              <div className="col-xl-2 col-lg-2 col-md-2 col-sm-4 col-xs-6 ms-auto d-flex justify-content-end align-items-center mt-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setIsAddFilters(true)}
                >
                  Add Filters
                </button>
              </div>
            </div>

            {/* <div className="row align-items-center">
              {alerts?.some((alert) => alert.selected === true) && (
                <div className="col-lg-2 col-md-2 col-sm-6 col-xs-12 d-flex justify-content-end align-items-center gap-3 ms-auto">
                  <div className="flex-grow-1">
                    <Form.Group controlId="statusSelect" >
                      <Form.Select
                        size="xs"
                        value={alertStatus}
                        onChange={(e) => toggleAllStatus(e.target.value)}
                      >
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                  <Button variant="outline-primary" onClick={handleSubmit}>
                    Update
                  </Button>
                </div>
              )}
              <div className="add-filter mb-1">
                {selectedFilters?.map((filter, index) => (
                  <div key={index} className="custome-filters">
                    {filter.property} : {filter.value}
                  </div>
                ))}
              </div>
            </div> */}

            <div className="row align-items-center justify-content-between">
              {/* Selected Filters (left side) */}
              <div className="col d-flex flex-wrap align-items-center gap-2">
                {selectedFilters?.map((filter, index) => (
                  <div key={index} className="custome-filters">
                    {filter.property} : {filter.value}
                  </div>
                ))}
              </div>

              {/* Dropdown + Update Button (right side) */}
              {alerts?.some((alert) => alert.selected === true) && (
                <div className="col-auto d-flex align-items-center gap-3">
                  <Form.Group controlId="statusSelect" className="mb-0">
                    <Form.Select
                      size="xs"
                      value={alertStatus}
                      onChange={(e) => toggleAllStatus(e.target.value)}
                    >
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </Form.Select>
                  </Form.Group>
                  <Button variant="outline-primary" onClick={handleSubmit}>
                    Update
                  </Button>
                </div>
              )}
            </div>
            {/* <div className="add-filter mb-1">
              {selectedFilters?.map((filter, index) => (
                <div key={index} className="custome-filters">
                  {filter.property} : {filter.value}
                </div>
              ))}
            </div> */}

            {isAddFilters && (
              <>
                <Modal
                  show={isAddFilters}
                  onHide={() => {
                    setIsAddFilters(false);
                    setFilters(null);
                  }}
                  size="md"
                  centered
                >
                  <Modal.Header className="d-flex align-items-center justify-content-between">
                    <Modal.Title className="fs-5 fw-bold icon-color mb-0">
                      Add Filters
                    </Modal.Title>

                    <Button
                      variant="light"
                      onClick={() => setIsAddFilters(false)}
                      className="border-0 p-0 ms-auto"
                    >
                      <FaTimes color="red" size={20} />
                    </Button>
                  </Modal.Header>

                  <hr className="m-0" />

                  <Modal.Body>
                    <div className="row d-flex align-items-center">
                      <div className=" col-lg-6 col-md-6 col-sm-12 col-xs-12 search-ecs">
                        <Select
                          className="custom-select"
                          value={filters ? { label: filters, value: filters } : null}
                          onChange={(selected) => {
                            const value = selected?.value || "";
                            setFilters(value);
                            fetchOptions(value);
                          }}
                          options={
                            propertyOptions
                              ?.filter(option => option !== "@timestamp" && option !== "@version")
                              .map(option => ({ label: option, value: option })) || []
                          }
                          placeholder="Select a filter"
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            option: (base) => ({
                              ...base,
                              fontSize: "1rem",
                              fontWeight: "400",
                            }),
                            placeholder: (base) => ({
                              ...base,
                              fontSize: "0.95rem",
                            }),
                            menuPortal: base => ({ ...base, zIndex: 9999 }), // Ensure it's on top
                          }}
                        />

                      </div>

                      <div className="col-lg-6 col-md-6 col-sm-6 col-xs-6 search-ecs">
                        <Form.Group controlId="searchSelect" >
                          <Select
                            className="custom-select"
                            value={searchQuery ? { label: searchQuery, value: searchQuery } : null}
                            onChange={(selected) => handleSearchChange(null, selected?.value || "")}
                            options={options.map((opt) => ({ label: opt, value: opt }))}
                            placeholder="Please Enter Input"
                            isClearable
                            styles={{
                              option: (base) => ({
                                ...base,
                                fontSize: "1rem",
                                fontWeight: "400",
                              }),
                              placeholder: (base) => ({
                                ...base,
                                fontSize: "0.95rem",
                              }),
                            }}
                          />
                        </Form.Group>
                      </div>
                    </div>
                    <div className="add-filter">
                      {selectedFilters?.map((filter, index) => (
                        <div key={index} className="custome-filters">
                          {filter.property} : {filter.value}
                          <button
                            className="close-btn"
                            onClick={() => handleRemoveFilter(filter.property)}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="row d-flex justify-content-center my-2">
                      <div className="col-lg-1 col-md-1 col-sm-1 col-xs-1 mx-4 ">
                        <button
                          type="button"
                          class="btn btn-outline-primary"
                          disabled={!searchQuery || !filters}
                          onClick={() => {
                            handleSelectedFilters(searchQuery);
                            setSearchQuery("");
                            setIsAddFilters(false);
                            setFilters(null)
                          }}
                        >
                          Add
                        </button>
                      </div>
                      <div className="col-lg-4 col-md-4 col-sm-5 col-xs-5 ">
                        <button
                          type="button"
                          class="btn btn-outline-danger"
                          onClick={handleClearFilters}
                        >
                          Clear Filters
                        </button>
                      </div>
                    </div>
                  </Modal.Body>
                </Modal>
              </>
            )}
            <>
              <div className="card table-responsive mt-3">
                <table class=" table bootstrap-table">
                  <thead>
                    <tr>
                      <th align="center">
                        <input
                          type="checkbox"
                          id="checkbox-head"
                          checked={isAllSelected}
                          disabled={alerts?.length === 0}
                          onChange={(e) => toggleAll(e.target.checked)}
                        />
                      </th>
                      <th align="center">Incident Time</th>
                      <th align="center">Agent Name</th>
                      <th align="center">Agent IP</th>
                      <th className="wrap">    Description  </th>
                      {properties?.length > 0 &&
                        properties
                          ?.filter(
                            (prop) =>
                              prop !== "rule.description" &&
                              prop !== "@timestamp" &&
                              prop !== "agent.ip" &&
                              prop !== "agent.name"
                          )
                          ?.map((prop, index) => (
                            <th key={index}>
                              <div className="table-cell-content">
                                <span>{prop}</span>
                                <button
                                  className="close-btn"
                                  onClick={() => handleRemoveProp(prop)}
                                >
                                  -
                                </button>
                              </div>
                            </th>
                          ))}
                      <th align="center">Severity</th>
                      <th align="center"> Status </th>
                      <th align="center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alerts.length > 0 ? (
                      alerts?.map((alert, index) => (
                        <tr key={index}>
                          <td className="custome-tb" align="center">
                            <input
                              type="checkbox"
                              checked={alert?.selected}
                              onChange={() => toggleRow(alert.alertId)}
                            />
                          </td>
                          <td
                            className="custome-tb text-primary"
                            align="center"
                          >
                            <b>{alert.timestamp || "-"}</b>
                          </td>
                          <td className="custome-tb" align="center">
                            {alert.agentName || "-"}
                          </td>
                          <td
                            className="custome-tb text-secondary"
                            align="center"

                          >
                            <b>{alert.agentip || "-"}</b>
                          </td>
                          <td
                            className="custome-tb wrap"
                            style={{ minWidth: "150px" }}
                          >
                            {alert.ruleDescription &&
                              alert.ruleDescription.length > 70
                              ? alert.ruleDescription.slice(0, 70) + "..."
                              : alert.ruleDescription || "-"}
                          </td>
                          {properties?.length > 0 &&
                            properties
                              ?.filter(
                                (prop) =>
                                  prop !== "rule.description" &&
                                  prop !== "@timestamp" &&
                                  prop !== "agent.ip" &&
                                  prop !== "agent.name"
                              )
                              ?.map((prop, propIndex) => (
                                <td
                                  className="custome-tb"
                                  key={propIndex}
                                  sx={{
                                    whiteSpace:
                                      getPropertyValue(alert.source, prop)
                                        .length > 60
                                        ? "normal"
                                        : "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: "200px",
                                    minWidth: "150px",
                                  }}
                                >
                                  {getPropertyValue(alert.source, prop)
                                    .length > 60 &&
                                    typeof getPropertyValue(
                                      alert.source,
                                      prop
                                    ) === "string"
                                    ? getPropertyValue(
                                      alert.source,
                                      prop
                                    ).substring(0, 60) + "..."
                                    : typeof getPropertyValue(
                                      alert.source,
                                      prop
                                    ) === "object"
                                      ? getPropertyValue(
                                        alert.source,
                                        prop
                                      )?.map((prop) => (
                                        <li className=".file-list" >
                                          {prop}
                                        </li>
                                      ))
                                      : getPropertyValue(alert.source, prop)}
                                </td>
                              ))}
                          <td align="center" className="custome-tb">
                            <div className="rating-value d-flex justify-content-center">
                              <span
                                className={`${getSeverityLabel(alert.ruleLevel)
                                  ? getSeverityLabel(
                                    alert.ruleLevel
                                  ).toLowerCase()
                                  : ""
                                  } p-2 m-0 d-block`}
                                style={{ width: "90px" }}
                              >
                                {getSeverityLabel(alert.ruleLevel)}
                              </span>
                            </div>
                          </td>
                          <td className="custome-tb">
                            <Form.Group controlId="statusSelect" >
                              <Form.Select
                                size="xs"
                                value={alert.alertstatus}
                                disabled={!alert?.selected}
                                onChange={(e) => toggleAlertStatus(
                                  alert.alertId,
                                  e.target.value
                                )}
                              >
                                <option value="true">True</option>
                                <option value="false">False</option>
                              </Form.Select>
                            </Form.Group>
                          </td>
                          <td align="center" className="custome-tb">
                            <span className="d-flex  justify-content-center gap-2  mx-3">
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id={`tooltip-view`}>View</Tooltip>}
                              >
                                <img
                                  src="/assets/viewicon.svg"
                                  alt="View"
                                  className="icons-style"
                                  onClick={() => viewAlerts(alert)}
                                />
                              </OverlayTrigger>

                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-download">Download</Tooltip>}
                              >
                                <span style={{ cursor: 'pointer' }} className="mt-2">
                                  <FaDownload
                                    color="lightblue"
                                    size={24}
                                    onClick={() => handleDownloadExcel(alert?.alertId)}
                                  />
                                </span>
                              </OverlayTrigger>
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={8 + (properties?.length ?? 0)}
                          align="center"
                        >
                          <FaExclamationTriangle size={18} color="gray" className="mx-2" />
                          {isSOCENV
                            ? "No Data Available"
                            : "Please Contact for Administrator Support"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <CustomPagination
                count={totalCount}
                rowsPerPageOptions={[20, 25, 50, 100, 250, 500]}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
              {

                <Modal
                  show={open}
                  onHide={handleCancel}
                  aria-labelledby="alert-dialog-title"
                  aria-describedby="alert-dialog-description"
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title id="alert-dialog-title">Save changes</Modal.Title>
                  </Modal.Header>

                  <Modal.Body>
                    <p id="alert-dialog-description">
                      You have unsaved changes. Are you sure you want to leave this page?
                    </p>
                  </Modal.Body>

                  <Modal.Footer>
                    <Button
                      variant="danger"
                      onClick={handleCancel}
                      className="customecancel-button"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleSaveChanges}
                      className="customesave-button"
                    >
                      Save
                    </Button>
                  </Modal.Footer>
                </Modal>
              }
            </>

            {viewAlert && loading ? (
              <div
                className="d-flex justify-content-center mt-4"
              >
                <Spinner animation="border" variant="primary" />
              </div>
            ) : (
              <>

                <Modal
                  show={dialogOpen}
                  onHide={() => setDialogOpen(false)}
                  size="lg"
                  centered
                >
                  <Modal.Header className="d-flex justify-content-between align-items-center">
                    <Modal.Title className="fs-5 fw-bold icon-color">
                      {selectedAlert?._source?.rule?.description}
                    </Modal.Title>
                    <Button variant="light" onClick={() => setDialogOpen(false)} className="border-0 p-0">
                      <FaTimes color="red" size={20} />
                    </Button>
                  </Modal.Header>

                  <hr className="m-0" />

                  <Modal.Body>
                    {selectedAlert && (
                      <div className="d-flex justify-content-center">
                        <div style={{ width: "75%" }}>
                          <div className="row my-2">
                            <div className="col-5 text-body1">
                              <strong className="word-wrap">
                                Alert Occurred On:
                              </strong>
                            </div>
                            <div className="col-7 text-body1 text-grey-8">
                              {formatTime(selectedAlert?._source?.timestamp)}
                            </div>
                          </div>
                          {selectedAlert?._source?.agent &&
                            Object.keys(selectedAlert?._source?.agent)
                              .filter((key) => key !== "id" && key !== "labels")
                              .map((key, index) => (
                                <div
                                  className="row my-2"
                                  key={`agent-${index}`}
                                >
                                  <div className="col-5 text-body1">
                                    <strong className="word-wrap">
                                      {formatPropertyName(key)}
                                    </strong>
                                    :
                                  </div>
                                  <div className="col-7 text-body1 text-grey-8">
                                    {renderTTPsValue(
                                      key,
                                      selectedAlert?._source?.agent[key]
                                    )}
                                  </div>
                                </div>
                              ))}

                          {selectedAlert?._source?.rule &&
                            Object.keys(selectedAlert?._source?.rule)
                              .filter((key) =>
                                name !== "vulnerability"
                                  ? key !== "mitre" &&
                                  key === "level" &&
                                  key !== "mail" &&
                                  key !== "description" &&
                                  key !== "firedtimes" &&
                                  key !== "groups" &&
                                  key !== "pci_dss" &&
                                  key !== "hipaa" &&
                                  key !== "tsc" &&
                                  key !== "id" &&
                                  key !== "nist_800_53" &&
                                  key !== "gdpr"
                                  : key !== "mitre" &&
                                  key === "level" &&
                                  key !== "mail" &&
                                  key !== "description" &&
                                  key !== "firedtimes" &&
                                  key !== "groups" &&
                                  key !== "pci_dss" &&
                                  key !== "hipaa" &&
                                  key !== "tsc" &&
                                  key !== "id" &&
                                  key !== "nist_800_53" &&
                                  key !== "gdpr"
                              )
                              .map((key, index) => (
                                <div className="row my-2" key={`rule-${index}`}>
                                  <div className="col-5 text-body1">
                                    <strong className="word-wrap">
                                      {formatPropertyName(key)}
                                    </strong>
                                    :
                                  </div>
                                  <div className="col-7 text-body1 text-grey-8">
                                    {renderTTPsValue(
                                      key,
                                      selectedAlert?._source?.rule[key]
                                    )}
                                  </div>
                                </div>
                              ))}
                          {selectedAlert?._source?.location && (
                            <div className="row my-2">
                              <div className="col-5 text-body1">
                                <strong className="word-wrap">Location</strong>:
                              </div>
                              <div className="col-7 text-body1 text-grey-8">
                                {selectedAlert?._source?.location}
                              </div>
                            </div>
                          )}
                          {selectedAlert?._source?.full_log && (
                            <div className="row my-2">
                              <div className="col-5 text-body1">
                                <strong className="word-wrap">Full Log</strong>:
                              </div>
                              <div className="col-7 text-body1 text-grey-8 word-wrap">
                                {isExpanded
                                  ? selectedAlert?._source?.full_log
                                  : selectedAlert?._source?.full_log?.slice(
                                    0,
                                    200
                                  )}
                                {selectedAlert?._source?.full_log?.length >
                                  200 && (
                                    <span
                                      onClick={() => setIsExpanded(!isExpanded)}
                                      className="expand-more"
                                    >
                                      {isExpanded ? "Read Less" : "Read More"}
                                    </span>
                                  )}
                              </div>
                            </div>
                          )}

                          {selectedAlert?._source?.data?.vulnerability && (
                            <div className="my-2">
                              <Accordion defaultActiveKey="0" className="mx-2">
                                <Card className="mb-3 shadow-sm rounded border-0">
                                  <Accordion.Item eventKey="0">
                                    <Accordion.Header onClick={() => toggleAccordion("one")}>
                                      <div className="d-flex w-100 justify-content-between">
                                        <strong>Vulnerability Data</strong>
                                        {openAccordions["one"] ? <FaChevronUp /> : <FaChevronDown />}
                                      </div>

                                    </Accordion.Header>
                                    <Accordion.Body>
                                      {selectedAlert?._source?.data
                                        ?.vulnerability &&
                                        Object.keys(
                                          selectedAlert?._source.data.vulnerability
                                        )
                                          .filter(
                                            (key) =>
                                              key !== "package" &&
                                              key !== "cvss" &&
                                              key !== "score" &&
                                              key !== "severity"
                                          )
                                          .filter(([key]) => key !== "score")
                                          .map((key, index) => (
                                            <div
                                              className="row my-2"
                                              key={`vulnerability-${index}`}
                                            >
                                              <div className="col-5 text-body1">
                                                <strong className="word-wrap">
                                                  {formatPropertyName(key)}
                                                </strong>
                                                :
                                              </div>
                                              <div className="col-7 text-body1 text-grey-8 word-wrap">
                                                {renderTTPsValue(
                                                  key,
                                                  selectedAlert?._source?.data
                                                    ?.vulnerability[key]
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                      {selectedAlert?._source?.data?.vulnerability
                                        ?.package &&
                                        Object.keys(
                                          selectedAlert?._source.data.vulnerability
                                            ?.package
                                        ).map((key, index) => (
                                          <div
                                            className="row my-2"
                                            key={`vulnerability-${index}`}
                                          >
                                            <div className="col-5 text-body1">
                                              <strong className="word-wrap">
                                                {formatPropertyName(key)}
                                              </strong>
                                              :
                                            </div>
                                            <div className="col-7 text-body1 text-grey-8">
                                              {renderTTPsValue(
                                                key,
                                                selectedAlert?._source?.data
                                                  ?.vulnerability?.package[key]
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      {selectedAlert?._source?.data?.vulnerability
                                        ?.package &&
                                        Object.keys(
                                          selectedAlert?._source.data.vulnerability
                                            ?.cvss
                                        ).map((key, index) => (
                                          <div
                                            className="row my-2"
                                            key={`vulnerability-${index}`}
                                          >
                                            <div className="col-5 text-body1">
                                              <strong className="word-wrap">
                                                {formatPropertyName(key)}
                                              </strong>
                                              :
                                            </div>
                                            <div className="col-7 text-body1 text-grey-8">
                                              {renderTTPsValue(
                                                key,
                                                selectedAlert?._source?.data
                                                  ?.vulnerability?.cvss[key]
                                                  ?.base_score
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                    </Accordion.Body>
                                  </Accordion.Item>
                                </Card>
                              </Accordion>
                            </div>
                          )}

                          {selectedAlert?._source?.data?.win?.eventdata && (
                            <div className="my-2">
                              <Accordion defaultActiveKey="0" className="mx-2">
                                <Card className="mb-3 shadow-sm rounded border-0">
                                  <Accordion.Item eventKey="0">
                                    <Accordion.Header onClick={() => toggleAccordion("two")}>
                                      <div className="d-flex w-100 justify-content-between">
                                        <strong>Event Data</strong>
                                        {openAccordions["two"] ? <FaChevronUp /> : <FaChevronDown />}
                                      </div>

                                    </Accordion.Header>
                                    <Accordion.Body>
                                      {selectedAlert?._source?.data?.win
                                        ?.eventdata &&
                                        Object.entries(
                                          selectedAlert?._source.data.win.eventdata
                                        ).map(([key, value], index) => (
                                          <div
                                            className="row my-2"
                                            key={`eventdata-${index}`}
                                          >
                                            <div className="col-5 text-body1">
                                              <strong className="word-wrap">
                                                {formatPropertyName(key)}
                                              </strong>
                                              :
                                            </div>
                                            <div className="col-7 text-body1 text-grey-8 word-wrap">
                                              {renderTTPsValue(key, value)}
                                            </div>
                                          </div>
                                        ))}
                                    </Accordion.Body>
                                  </Accordion.Item>
                                </Card>
                              </Accordion>
                            </div>
                          )}
                          {selectedAlert?._source?.data?.win?.system && (
                            <div className="my-2">
                              <Accordion defaultActiveKey="0" className="mx-2">
                                <Card className="mb-3 shadow-sm rounded border-0">
                                  <Accordion.Item eventKey="0">
                                    <Accordion.Header>

                                    </Accordion.Header>
                                    <Accordion.Header onClick={() => toggleAccordion("three")}>
                                      <div className="d-flex w-100 justify-content-between">
                                        <strong>System Data</strong>
                                        {openAccordions["three"] ? <FaChevronUp /> : <FaChevronDown />}
                                      </div>

                                    </Accordion.Header>
                                    <Accordion.Body>
                                      {selectedAlert?._source?.data?.win?.system &&
                                        Object.entries(
                                          selectedAlert?._source.data.win.system
                                        )
                                          .filter(
                                            ([key]) =>
                                              key !== "message" && key !== "level"
                                          )
                                          .map(([key, value], index) => (
                                            <div
                                              className="row my-2"
                                              key={`system-${index}`}
                                            >
                                              <div className="col-5 text-body1">
                                                <strong className="word-wrap">
                                                  {formatPropertyName(key)}
                                                </strong>
                                                :
                                              </div>
                                              <div className="col-7 text-body1 text-grey-8 word-wrap">
                                                {renderTTPsValue(key, value)}
                                              </div>
                                            </div>
                                          ))}
                                      {selectedAlert?._source?.data?.win?.system
                                        ?.message && (
                                          <div className="row my-2">
                                            <div className="col-5 text-body1">
                                              <strong className="word-wrap">
                                                Message
                                              </strong>
                                              :
                                            </div>
                                            <div className="col-7 text-body1 text-grey-8">
                                              {selectedAlert?._source?.data?.win
                                                ?.system?.message?.length > 250 &&
                                                !expand ? (
                                                <p className="col">
                                                  {selectedAlert?._source?.data?.win?.system?.message?.slice(
                                                    0,
                                                    250
                                                  )}
                                                  ...
                                                  <span
                                                    className="expand-more"
                                                    onClick={() => {
                                                      setExpand(!expand);
                                                    }}
                                                  >
                                                    Read more
                                                  </span>
                                                </p>
                                              ) : (
                                                <p className="col">
                                                  {
                                                    selectedAlert?._source?.data?.win
                                                      ?.system?.message
                                                  }
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                    </Accordion.Body>
                                  </Accordion.Item>
                                </Card>
                              </Accordion>
                            </div>
                          )}

                          {selectedAlert?._source?.syscheck && (
                            <div className="my-2">
                              <Accordion defaultActiveKey="0" className="mx-2">
                                <Card className="mb-3 shadow-sm rounded border-0">
                                  <Accordion.Item eventKey="0">
                                    <Accordion.Header onClick={() => toggleAccordion("four")}>
                                      <div className="d-flex w-100 justify-content-between">
                                        <strong>Syscheck Data</strong>
                                        {openAccordions["four"] ? <FaChevronUp /> : <FaChevronDown />}
                                      </div>
                                    </Accordion.Header>
                                    <Accordion.Body>
                                      {selectedAlert?._source?.syscheck &&
                                        Object.entries(
                                          selectedAlert?._source?.syscheck
                                        ).map(([key, value], index) => (
                                          <div
                                            className="row my-2"
                                            key={`eventdata-${index}`}
                                          >
                                            <div className="col-5 text-body1">
                                              <strong className="word-wrap">
                                                {formatPropertyName(key)}
                                              </strong>
                                              :
                                            </div>
                                            <div className="col-7 text-body1 text-grey-8 word-wrap">
                                              {renderValue(value)}
                                            </div>
                                          </div>
                                        ))}
                                    </Accordion.Body>
                                  </Accordion.Item>
                                </Card>
                              </Accordion>
                            </div>
                          )}

                          {selectedAlert?._source?.data &&
                            selectedAlert?._source?.data?.win === undefined &&
                            selectedAlert?._source.data.vulnerability ===
                            undefined && (
                              <div className="my-2">
                                <Accordion defaultActiveKey="0" className="mx-2">
                                  <Card className="mb-3 shadow-sm rounded border-0">
                                    <Accordion.Item eventKey="0">
                                      <Accordion.Header>

                                      </Accordion.Header>
                                      <Accordion.Header onClick={() => toggleAccordion("five")}>
                                        <div className="d-flex w-100 justify-content-between">
                                          <strong>Data</strong>
                                          {openAccordions["five"] ? <FaChevronUp /> : <FaChevronDown />}
                                        </div>
                                      </Accordion.Header>
                                      <Accordion.Body>
                                        {selectedAlert?._source?.data &&
                                          Object.entries(selectedAlert._source.data).map(([key, value], index) =>
                                            typeof value === "object" && !Array.isArray(value) ? (
                                              <div className="col-lg-12" key={`nested-${index}`}>
                                                {renderSourceDataValue(value, key)}
                                              </div>
                                            ) : (
                                              <div className="row my-2" key={`eventdata-${index}`}>
                                                <div className="col-5" style={{ fontSize: "1rem" }}>
                                                  <strong className="word-wrap">{key}</strong>:
                                                </div>
                                                <div
                                                  className="col-7 text-muted word-wrap"
                                                  style={{ fontSize: "1rem" }}
                                                >
                                                  {renderSourceDataValue(value, key)}
                                                </div>
                                              </div>
                                            )
                                          )}
                                      </Accordion.Body>
                                    </Accordion.Item>
                                  </Card>
                                </Accordion>
                              </div>
                            )}

                          {selectedAlert?._source?.rule?.mitre && (
                            <Card className="mx-2 my-2 shadow-sm rounded">
                              <Card.Header className="border-bottom">
                                <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>Mitre Details</span>
                              </Card.Header>

                              <Card.Body className="mx-2">
                                {selectedAlert?._source?.rule?.mitre &&
                                  Object.keys(
                                    selectedAlert?._source.rule?.mitre
                                  ).map((key, index) => (
                                    <div
                                      className="row my-2"
                                      key={`mitre-${index}`}
                                    >
                                      <div className="col-5 text-body1 p-0">
                                        <strong className="word-wrap">
                                          {formatPropertyName(key)}
                                        </strong>
                                        :
                                      </div>
                                      <div className="col-7 text-body1 text-grey-8">
                                        {renderTTPsValue(
                                          key,
                                          selectedAlert?._source?.rule?.mitre[
                                          key
                                          ]
                                        )}
                                      </div>
                                    </div>
                                  ))}
                              </Card.Body>
                            </Card>
                          )}
                        </div>
                      </div>
                    )}

                  </Modal.Body>
                </Modal>

              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ReportManagement;
